import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import client from '../../../client';
import { useMessage } from 'app/shared-components/MessageUtils';
import { useTranslation } from 'react-i18next';

const ModalEditContainerTank = ({ open, setOpen, tankData, onUpdated, compacctnum }) => {
  const { t } = useTranslation('navigation');
  const showMessage = useMessage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    location: '',
    capacity: '',
    notes: '',
    show_in_dashboard: true,
  });

  useEffect(() => {
    if (tankData && open) {
      // Format capacity with commas when loading existing data
      const formatCapacity = (value) => {
        if (!value) return '';
        const numericValue = value.toString().replace(/[^0-9.]/g, '');
        if (numericValue) {
          const parts = numericValue.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return parts.join('.');
        }
        return '';
      };

      setFormData({
        location: tankData.location || '',
        capacity: formatCapacity(tankData.capacity),
        notes: tankData.notes || '',
        show_in_dashboard: tankData.show_in_dashboard !== undefined ? tankData.show_in_dashboard : true,
      });
    }
  }, [tankData, open]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSave = async () => {
    if (!tankData?.id) {
      showMessage('Tank ID is required', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        location: formData.location,
        capacity: parseFloat(formData.capacity.replace(/,/g, '')) || 0,
        notes: formData.notes,
        show_in_dashboard: formData.show_in_dashboard,
        tankId: tankData.id,
      };
      
      console.log('Updating tank with payload:', payload);
      console.log('API endpoint:', `/fuel-inventory/update-tank/${compacctnum}`);
      
      const response = await client.put(`/fuel-inventory/update-tank/${compacctnum}`, payload);

      if (response && response.data) {
        showMessage('Tank updated successfully', 'success');
        onUpdated && onUpdated();
        handleClose();
      } else {
        showMessage('Failed to update tank', 'error');
      }
    } catch (error) {
      console.error('Error updating tank:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update tank. Please try again.';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      location: '',
      capacity: '',
      notes: '',
      show_in_dashboard: true,
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Typography variant="h6" component="div">
          {t('EDIT_TANK')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'black', 
                    fontWeight: '400',
                    mb: 1,
                    fontSize: '1.3rem'
                  }}
                >
                  {t('TANK_NICKNAME')}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  placeholder="Enter tank location"
                  inputProps={{
                    style: {
                      paddingTop: '7px',
                      paddingBottom: '7px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.3rem'
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'text'
                    }
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'black', 
                    fontWeight: '400',
                    mb: 1,
                    fontSize: '1.3rem'
                  }}
                >
                  {t('CAPACITY_LABEL')}
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  value={formData.capacity}
                  onChange={handleInputChange('capacity')}
                  placeholder="Enter tank capacity in units"
                  inputProps={{
                    style: {
                      paddingTop: '7px',
                      paddingBottom: '7px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.3rem'
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'text'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'black', 
                  fontWeight: '400',
                  mb: 1,
                  fontSize: '1.3rem'
                }}
              >
                {t('NOTES')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                placeholder="Enter any additional notes"
                inputProps={{
                  style: {
                    paddingTop: '7px',
                    paddingBottom: '7px'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.3rem'
                  },
                  '& .MuiInputBase-input': {
                    cursor: 'text'
                  }
                }}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.show_in_dashboard}
                    onChange={handleCheckboxChange('show_in_dashboard')}
                    color="primary"
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.5rem'
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'black', 
                        fontWeight: '400',
                        fontSize: '1.3rem'
                      }}
                    >
                      {t('SHOW_IN_DASHBOARD')}
                    </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {t('SHOW_IN_DASHBOARD_EXPLANATION')}
                          </Typography>
                          <Box
                            component="img"
                            src="/assets/images/dashboard-fuel-chart.png"
                            alt="Dashboard Fuel Chart"
                            sx={{
                              width: '80%',
                              maxWidth: '300px',
                              height: 'auto',
                              borderRadius: '8px',
                              border: '2px solid rgba(255, 255, 255, 0.1)',
                              mt: 1,
                            }}
                            onError={(e) => {
                              // Fallback si la imagen no existe
                              e.target.style.display = 'none';
                            }}
                          />
                        </Box>
                      }
                      arrow
                      placement="top"
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: '550px',
                          backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        }
                      }}
                    >
                      <IconButton size="small" sx={{ color: '#1976d2' }}>
                        <InfoIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          {t('CANCEL')}
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('SAVING') : t('SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditContainerTank;
